import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Download, Loader2, FileSpreadsheet, FileText, AlertCircle, X, Plus, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import WorldGlobe from '../components/ui/WorldGlobe';
import SalesforceExtraFieldsPanel from '../components/export/SalesforceExtraFieldsPanel';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

const Export = () => {
  const { token, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searches, setSearches] = useState([]);
  const [selectedSearchId, setSelectedSearchId] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  // CL-SF-EXPORT-001: 'standard' (unchanged) or 'salesforce' (Salesforce Extra Fields preset)
  const [preset, setPreset] = useState('standard');
  
  // Column configuration state
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [availableColumns, setAvailableColumns] = useState({});
  const [configLoading, setConfigLoading] = useState(true);
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  
  // Drag state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Load export config
  const fetchExportConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/export-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedColumns(response.data.selected_columns);
      setAvailableColumns(response.data.available_columns);
    } catch (error) {
      // Fallback to default if config load fails
      console.error('Failed to load export config:', error);
    } finally {
      setConfigLoading(false);
    }
  }, [token]);

  // Save export config
  const saveExportConfig = async (columns) => {
    try {
      await axios.put(
        `${API_URL}/export-config`,
        { selected_columns: columns },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      toast.error('Failed to save column configuration');
    }
  };

  // Load searches with decision counts
  useEffect(() => {
    const fetchSearches = async () => {
      try {
        const response = await axios.get(`${API_URL}/exports/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSearches(response.data);
        
        // Pre-select search from URL parameter (from Reports navigation)
        const preSelectId = searchParams.get('search');
        if (preSelectId) {
          const matchingSearch = response.data.find(s => s.search_id === preSelectId);
          if (matchingSearch) {
            setSelectedSearchId(preSelectId);
          }
          // Clear the URL parameter
          setSearchParams({});
        }
      } catch (error) {
        toast.error('Failed to load searches');
      } finally {
        setLoading(false);
      }
    };
    fetchSearches();
    fetchExportConfig();
  }, [token, searchParams, setSearchParams, fetchExportConfig]);

  const selectedSearch = searches.find(s => s.search_id === selectedSearchId);
  const approvedCount = selectedSearch?.approved_count || 0;
  const canExport = approvedCount > 0;

  // Get unselected columns (available to add)
  const unselectedColumns = Object.entries(availableColumns)
    .filter(([key]) => !selectedColumns.includes(key));

  // Column management
  const handleRemoveColumn = (columnKey) => {
    if (selectedColumns.length <= 1) {
      toast.error('At least one column is required');
      return;
    }
    const newColumns = selectedColumns.filter(c => c !== columnKey);
    setSelectedColumns(newColumns);
    saveExportConfig(newColumns);
  };

  const handleAddColumn = (columnKey) => {
    const newColumns = [...selectedColumns, columnKey];
    setSelectedColumns(newColumns);
    saveExportConfig(newColumns);
    setAddColumnOpen(false);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Add a drag image
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder columns
    const newColumns = [...selectedColumns];
    const [draggedItem] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(dropIndex, 0, draggedItem);
    
    setSelectedColumns(newColumns);
    saveExportConfig(newColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleExport = async (format) => {
    if (!selectedSearchId || !canExport) return;
    
    setExporting(true);
    try {
      const response = await axios.get(
        `${API_URL}/exports/${selectedSearchId}?format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Get filename from response header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `ciaolead_approved.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${approvedCount} approved leads as ${format.toUpperCase()}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Export failed';
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSearchLabel = (search) => {
    const categories = search.parameters?.categories?.join(', ') || 'All';
    const date = formatDate(search.created_at);
    return `${categories} · ${date}`;
  };

  if (loading || configLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 relative" data-testid="export-page">
      <WorldGlobe size={300} className="hidden lg:block opacity-70" style={{ top: '-50px', right: '-30px' }} />
      <Card className="world-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center world-icon-box">
              <Download className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Export Approved Leads</CardTitle>
              <CardDescription className="text-slate-400">
                Download your approved leads as CSV or Excel
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {searches.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center mb-4">
                <Download className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                No searches found
              </h3>
              <p className="text-slate-400 max-w-md">
                Run a search first and approve some leads to export them.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Search Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-200">
                  Select a search to export
                </label>
                <Select value={selectedSearchId} onValueChange={setSelectedSearchId}>
                  <SelectTrigger className="w-full" data-testid="search-selector">
                    <SelectValue placeholder="Choose a search..." />
                  </SelectTrigger>
                  <SelectContent>
                    {searches.map((search) => (
                      <SelectItem key={search.search_id} value={search.search_id}>
                        <div className="flex items-center justify-between gap-4">
                          <span className="truncate">{getSearchLabel(search)}</span>
                          <Badge 
                            variant={search.approved_count > 0 ? 'default' : 'secondary'}
                            className={search.approved_count > 0 ? 'bg-green-600' : ''}
                          >
                            {search.approved_count} approved
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selected Search Details */}
              {selectedSearch && (
                <div className="border border-white/10 rounded-lg p-4 bg-white/5">
                  <h4 className="font-medium text-white mb-3">Search Summary</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-400">Total Leads</p>
                      <p className="font-semibold text-white">{selectedSearch.total_leads}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Approved</p>
                      <p className="font-semibold text-green-400">{selectedSearch.approved_count}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Rejected</p>
                      <p className="font-semibold text-red-400">{selectedSearch.rejected_count}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Pending</p>
                      <p className="font-semibold text-slate-300">{selectedSearch.pending_count}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Export preset */}
              <div className="flex gap-2" role="group" aria-label="Export preset" data-testid="export-preset-selector">
                {[['standard', 'Standard Export'], ['salesforce', 'Salesforce Extra Fields']].map(([value, label]) => (
                  <Button
                    key={value}
                    variant={preset === value ? 'default' : 'outline'}
                    size="sm"
                    aria-pressed={preset === value}
                    onClick={() => setPreset(value)}
                    data-testid={`export-preset-${value}`}
                  >
                    {label}
                  </Button>
                ))}
              </div>

              {preset === 'salesforce' && (
                <SalesforceExtraFieldsPanel
                  token={token}
                  searchId={selectedSearchId}
                  canExport={canExport}
                  isAdmin={isAdmin}
                />
              )}

              {/* Export Buttons */}
              {preset === 'standard' && selectedSearchId && (
                <div className="space-y-3">
                  {!canExport ? (
                    <div className="flex items-center gap-2 p-4 border border-amber-500/40 bg-amber-500/10 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0" />
                      <p className="text-sm text-amber-300">
                        No approved leads to export. Approve some leads first from the Search page.
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={() => handleExport('csv')}
                        disabled={exporting}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        data-testid="export-csv-btn"
                      >
                        {exporting ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <FileText className="mr-2 h-4 w-4" />
                        )}
                        Export as CSV
                      </Button>
                      <Button
                        onClick={() => handleExport('xlsx')}
                        disabled={exporting}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        data-testid="export-xlsx-btn"
                      >
                        {exporting ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="mr-2 h-4 w-4" />
                        )}
                        Export as Excel
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Column Configuration */}
              {preset === 'standard' && (
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-slate-200">
                    Export columns ({selectedColumns.length})
                  </p>
                  {unselectedColumns.length > 0 && (
                    <Popover open={addColumnOpen} onOpenChange={setAddColumnOpen}>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-7 text-xs"
                          data-testid="add-column-btn"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add column
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-2" align="end">
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                          <p className="text-xs font-medium text-slate-400 px-2 py-1">
                            Available columns
                          </p>
                          {unselectedColumns.map(([key, label]) => (
                            <button
                              key={key}
                              onClick={() => handleAddColumn(key)}
                              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-white/10 transition-colors"
                              data-testid={`add-column-${key}`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                
                {/* Column Chips with Drag & Drop */}
                <div className="flex flex-wrap gap-2" data-testid="column-chips">
                  {selectedColumns.map((columnKey, index) => (
                    <div
                      key={columnKey}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`
                        inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                        bg-white/10 text-slate-200 cursor-grab active:cursor-grabbing
                        border border-transparent transition-all
                        ${draggedIndex === index ? 'opacity-50' : ''}
                        ${dragOverIndex === index ? 'border-blue-400 bg-blue-500/10' : ''}
                        hover:bg-white/10
                      `}
                      data-testid={`column-chip-${columnKey}`}
                    >
                      <GripVertical className="h-3 w-3 text-slate-400" />
                      <span>{availableColumns[columnKey] || columnKey}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveColumn(columnKey);
                        }}
                        className="ml-0.5 p-0.5 rounded-full hover:bg-white/15 transition-colors"
                        data-testid={`remove-column-${columnKey}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                
                <p className="text-xs text-slate-400 mt-2">
                  Drag to reorder. Click X to remove. Configuration is saved automatically.
                </p>
              </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Export;
